<!--
  PortalScopePill.vue (Client Portal v2 header store-scope switcher, ADR 0055).

  The topbar scope pill from the v2 design (chrome.jsx TopBar, ~lines 122-124):

    * Client Admin  -> a clickable "[store] <scope> [expand_more]" pill that
      opens a dropdown of the client's stores + an "All stores" aggregate
      option. Selecting one sets the GLOBAL portal scope (Pinia
      usePortalScopeStore) which re-scopes Overview / Orders / Approvals.
    * Store Manager -> a NON-interactive "[store] <their store> [lock]" chip.
      No dropdown; the caller is server-locked to a single store.

  Reads + writes the portalScope Pinia store; it does not fetch on its own
  (PortalLayout drives load()) so the pill and the pages share one scope. The
  Vuetify icon set is "md" (ligatures) per main.js -- icon names are bare
  ligatures ("store" / "expand_more" / "lock"), never "mdi-*".
-->
<template>
    <div class="portal-scope-pill" data-testid="portal-scope-pill">
        <!-- Store Manager: locked, non-interactive chip. -->
        <span
            v-if="scope.locked"
            class="portal-scope-pill__chip portal-scope-pill__chip--locked"
            data-testid="portal-scope-chip-locked"
        >
            <v-icon icon="store" size="15" class="portal-scope-pill__icon" />
            <span class="portal-scope-pill__label">{{ scope.scopeLabel }}</span>
            <v-icon icon="lock" size="13" class="portal-scope-pill__lock" />
        </span>

        <!-- Client Admin: dropdown of the client's stores + All stores. -->
        <v-menu
            v-else
            location="bottom start"
            data-testid="portal-scope-menu"
        >
            <template #activator="{ props }">
                <button
                    type="button"
                    class="portal-scope-pill__chip portal-scope-pill__chip--button"
                    :class="{ 'portal-scope-pill__chip--active': scope.isScoped }"
                    data-testid="portal-scope-trigger"
                    v-bind="props"
                >
                    <v-icon
                        icon="store"
                        size="15"
                        class="portal-scope-pill__icon"
                    />
                    <span class="portal-scope-pill__label">{{
                        scope.scopeLabel
                    }}</span>
                    <v-icon
                        icon="expand_more"
                        size="16"
                        class="portal-scope-pill__caret"
                    />
                </button>
            </template>

            <v-list density="compact" data-testid="portal-scope-list">
                <v-list-item
                    :active="!scope.isScoped"
                    data-testid="portal-scope-option-all"
                    @click="select(null)"
                >
                    <v-list-item-title>All stores</v-list-item-title>
                </v-list-item>
                <v-divider />
                <v-list-item
                    v-for="s in scope.stores"
                    :key="s.id"
                    :active="scope.selectedStoreId === s.id"
                    :data-testid="`portal-scope-option-${s.id}`"
                    @click="select(s.id)"
                >
                    <v-list-item-title>{{ s.name }}</v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu>
    </div>
</template>

<script>
import { usePortalScopeStore } from "@/stores/portalScope";

export default {
    name: "PortalScopePill",
    setup() {
        const scope = usePortalScopeStore();
        return { scope };
    },
    methods: {
        select(storeId) {
            this.scope.setStore(storeId);
        },
    },
};
</script>

<style scoped>
.portal-scope-pill {
    display: inline-flex;
    align-items: center;
}
.portal-scope-pill__chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: var(--tf-fg-2);
    background: var(--tf-surface-alt);
    border: 1px solid var(--tf-border);
    border-radius: 9999px;
    padding: 6px 13px;
}
.portal-scope-pill__chip--button {
    cursor: pointer;
    font-family: inherit;
}
.portal-scope-pill__chip--button:hover {
    background: var(--tf-surface-light, var(--tf-surface-alt));
}
.portal-scope-pill__chip--active {
    border-color: var(--portal-brand-color, var(--tf-border-strong));
    color: var(--tf-fg-1);
}
.portal-scope-pill__chip--locked {
    color: var(--tf-fg-3);
}
.portal-scope-pill__icon {
    color: var(--tf-fg-4);
}
.portal-scope-pill__chip--active .portal-scope-pill__icon {
    color: var(--portal-brand-color, var(--tf-fg-2));
}
.portal-scope-pill__caret {
    color: var(--tf-fg-4);
}
.portal-scope-pill__lock {
    color: var(--tf-fg-4);
}
.portal-scope-pill__label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 180px;
}
</style>
